import React, { useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  Form,
  Select,
  Switch,
  DatePicker,
  Typography,
  Row,
  Col,
  Button,
  Space,
  message,
  Input,
  InputNumber,
  Alert,
  Modal,
  Spin,
} from 'antd';
import { ArrowLeftOutlined, SaveOutlined, StopOutlined } from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import {
  useCalibrationSchedule,
  useCancelCalibrationSchedule,
  useCreateCalibrationSchedule,
  useUpdateCalibrationSchedule,
} from '../../hooks/useCalibration';
import assetService, { type AssetDto } from '../../services/asset.service';
import api from '../../services/api.service';
import type { CalibrationSchedule } from '../../services/calibration.service';
import { useAuthStore } from '../../store/authStore';
import { CALIBRATION_KIND_OPTIONS, INTERVAL_BASIS_OPTIONS } from '../tmmde/tmmdeDisplay';

const { Title, Text } = Typography;
const { TextArea } = Input;

type FormValues = {
  equipment_id: string;
  calibration_kind: CalibrationSchedule['calibration_kind'];
  calibration_type?: string;
  calibration_frequency_days: number;
  interval_basis: CalibrationSchedule['interval_basis'];
  first_use_date?: Dayjs | null;
  last_calibration_date?: Dayjs | null;
  next_calibration_date?: Dayjs | null;
  working_range?: string;
  accuracy?: string;
  measurement_unit?: string;
  calibration_standard?: string;
  acceptance_criteria?: string;
  calibration_provider?: string;
  supplier_id?: string | null;
  calibration_location?: string;
  external_limitations?: string;
  is_critical?: boolean;
};

const toDay = (v?: string | null) => (v ? dayjs(String(v).slice(0, 10)) : null);
const fromDay = (v?: Dayjs | null) => (v ? v.format('YYYY-MM-DD') : null);

function unwrapArray<T>(res: unknown): T[] {
  const ax = (res as { data?: { data?: T[] } })?.data;
  return Array.isArray(ax) ? ax : Array.isArray(ax?.data) ? (ax as { data: T[] }).data : [];
}

const CalibrationScheduleFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [search] = useSearchParams();
  const isEdit = Boolean(id);
  const [form] = Form.useForm<FormValues>();
  const user = useAuthStore((s) => s.user);
  const canCancel = Boolean(user?.is_super_admin || user?.permissions?.includes('calibration.schedule.edit'));
  const createMutation = useCreateCalibrationSchedule();
  const updateMutation = useUpdateCalibrationSchedule();
  const cancelMutation = useCancelCalibrationSchedule();
  const { data: existing, isLoading: loadingExisting } = useCalibrationSchedule(id);
  const intervalBasis = Form.useWatch('interval_basis', form);
  const lastDate = Form.useWatch('last_calibration_date', form);

  const { data: assets = [], isLoading: assetsLoading } = useQuery<AssetDto[]>({
    queryKey: ['assets', 'calibration-picklist'],
    queryFn: () => assetService.list(),
    staleTime: 5 * 60 * 1000,
  });

  const { data: suppliers = [] } = useQuery({
    queryKey: ['suppliers-calibration'],
    queryFn: async () => {
      try {
        return unwrapArray<{ id: string; supplier_name: string; supplier_code?: string }>(
          await api.get('/procurement/suppliers')
        );
      } catch {
        return [];
      }
    },
    staleTime: 10 * 60 * 1000,
  });

  const assetOptions = useMemo(
    () =>
      [...assets]
        .sort((a, b) => Number(Boolean(b.is_measuring_equipment)) - Number(Boolean(a.is_measuring_equipment)))
        .map((a) => ({
          value: a.id,
          label: `${a.asset_code} — ${a.asset_name}${a.is_measuring_equipment ? '' : ' (not yet flagged as measuring)'}`,
        })),
    [assets]
  );

  useEffect(() => {
    if (existing) {
      form.setFieldsValue({
        equipment_id: existing.equipment_id,
        calibration_kind: existing.calibration_kind || 'CALIBRATION',
        calibration_type: existing.calibration_type,
        calibration_frequency_days: existing.calibration_frequency_days,
        interval_basis: existing.interval_basis || 'CALENDAR',
        first_use_date: toDay(existing.first_use_date),
        last_calibration_date: toDay(existing.last_calibration_date),
        next_calibration_date: toDay(existing.next_calibration_date || existing.calibration_due_date),
        working_range: existing.working_range || undefined,
        accuracy: existing.accuracy || undefined,
        measurement_unit: existing.measurement_unit || undefined,
        calibration_standard: existing.calibration_standard,
        acceptance_criteria: existing.acceptance_criteria,
        calibration_provider: existing.calibration_provider,
        supplier_id: existing.supplier_id || undefined,
        calibration_location: existing.calibration_location,
        external_limitations: existing.external_limitations || undefined,
        is_critical: Boolean(existing.is_critical),
      });
    } else if (!isEdit) {
      const asset = search.get('asset');
      if (asset) form.setFieldValue('equipment_id', asset);
    }
  }, [existing, isEdit, search, form]);

  const backTo = () => {
    const eqId = existing?.equipment_id || search.get('asset');
    navigate(eqId ? `/qms/tmmde/${eqId}` : '/grc/calibration');
  };

  const onFinish = async (values: FormValues) => {
    const freq = Number(values.calibration_frequency_days);
    if (!Number.isFinite(freq) || freq <= 0) {
      message.error('Please enter a valid frequency in days.');
      return;
    }
    const payload: Partial<CalibrationSchedule> = {
      calibration_kind: values.calibration_kind,
      calibration_type: values.calibration_type,
      calibration_frequency_days: freq,
      interval_basis: values.interval_basis,
      first_use_date: fromDay(values.first_use_date),
      last_calibration_date: fromDay(values.last_calibration_date),
      working_range: values.working_range || null,
      accuracy: values.accuracy || null,
      measurement_unit: values.measurement_unit || null,
      calibration_standard: values.calibration_standard,
      acceptance_criteria: values.acceptance_criteria,
      calibration_provider: values.calibration_provider,
      supplier_id: values.supplier_id || null,
      calibration_location: values.calibration_location,
      external_limitations: values.external_limitations || null,
      is_critical: Boolean(values.is_critical),
    };
    const due = fromDay(values.next_calibration_date);
    const prevDue = existing ? (existing.next_calibration_date || existing.calibration_due_date || '').slice(0, 10) : '';
    if (due && due !== prevDue) {
      payload.next_calibration_date = due;
      payload.calibration_due_date = due;
    }
    if (isEdit && id) {
      const saved = await updateMutation.mutateAsync({ id, data: payload });
      navigate(`/qms/tmmde/${saved.equipment_id || existing?.equipment_id}`);
    } else {
      const saved = await createMutation.mutateAsync({ ...payload, equipment_id: values.equipment_id });
      navigate(`/qms/tmmde/${saved.equipment_id || values.equipment_id}`);
    }
  };

  const confirmCancel = () => {
    if (!id) return;
    let reason = '';
    Modal.confirm({
      title: 'Cancel this calibration schedule?',
      content: (
        <TextArea rows={3} placeholder="Reason (e.g. equipment disposed)" onChange={(e) => (reason = e.target.value)} />
      ),
      okText: 'Cancel schedule',
      okButtonProps: { danger: true },
      onOk: async () => {
        await cancelMutation.mutateAsync({ id, reason: reason.trim() || undefined });
        backTo();
      },
    });
  };

  if (isEdit && loadingExisting) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }

  const saving = createMutation.isPending || updateMutation.isPending;

  return (
    <div style={{ padding: '24px', maxWidth: 1000 }}>
      <Space align="center" style={{ marginBottom: 16 }}>
        <Button type="text" icon={<ArrowLeftOutlined />} onClick={backTo}>
          Back
        </Button>
      </Space>
      <Title level={2} style={{ marginTop: 0 }}>
        {isEdit ? `Edit calibration schedule ${existing?.calibration_code ?? ''}` : 'Add equipment to calibration'}
      </Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        Define how the equipment is calibrated or verified, how often, and the acceptance criteria.
      </Text>

      {!isEdit && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
          message="The asset is flagged as measuring equipment automatically"
          description="Enter the last calibration date to derive the due date, or the due date directly. For instruments kept unused in store, choose 'From first use' — the interval starts when the instrument is first issued."
        />
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{ calibration_kind: 'CALIBRATION', interval_basis: 'CALENDAR', is_critical: false }}
      >
        <Card title="Equipment and method" style={{ marginBottom: 16 }}>
          <Row gutter={24}>
            <Col xs={24}>
              <Form.Item name="equipment_id" label="Asset (equipment)" rules={[{ required: true, message: 'Select an asset' }]}>
                <Select
                  showSearch
                  disabled={isEdit}
                  placeholder="Select an asset from the register"
                  loading={assetsLoading}
                  options={assetOptions}
                  filterOption={(input, option) =>
                    (option?.label as string | undefined)?.toLowerCase().includes(input.toLowerCase()) ?? false
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="calibration_kind" label="Type" rules={[{ required: true }]}>
                <Select options={CALIBRATION_KIND_OPTIONS} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="calibration_type" label="Performed">
                <Select
                  allowClear
                  placeholder="Internal / external"
                  options={[
                    { value: 'Internal', label: 'Internal' },
                    { value: 'External', label: 'External' },
                    { value: 'Third Party', label: 'Third party' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="is_critical" label="Critical equipment" valuePropName="checked">
                <Switch checkedChildren="Yes" unCheckedChildren="No" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="working_range" label="Working range">
                <Input placeholder="e.g. 0–150 mm" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="accuracy" label="Accuracy / resolution">
                <Input placeholder="e.g. 0.02 mm" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="measurement_unit" label="Unit">
                <Input placeholder="mm, bar, °C…" />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="calibration_standard" label="Reference standard / method">
                <Input placeholder="Traceable standard or calibration procedure" />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="acceptance_criteria" label="Acceptance criteria" rules={[{ required: true }]}>
                <TextArea rows={3} placeholder="Tolerance / limits / pass-fail rules" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card title="Interval and dates" style={{ marginBottom: 16 }}>
          <Row gutter={24}>
            <Col xs={24} md={8}>
              <Form.Item name="calibration_frequency_days" label="Frequency (days)" rules={[{ required: true }]}>
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={16}>
              <Form.Item name="interval_basis" label="Interval basis" rules={[{ required: true }]}>
                <Select options={INTERVAL_BASIS_OPTIONS} />
              </Form.Item>
            </Col>
            {intervalBasis === 'FIRST_USE' && (
              <Col xs={24} md={8}>
                <Form.Item name="first_use_date" label="First use date" extra="Leave blank while the instrument is unused">
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            )}
            <Col xs={24} md={8}>
              <Form.Item name="last_calibration_date" label="Last calibration date">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="next_calibration_date"
                label="Due date"
                extra={lastDate ? 'Leave blank to derive from the last calibration date' : undefined}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card title="Calibration source" style={{ marginBottom: 16 }}>
          <Row gutter={24}>
            <Col xs={24} md={12}>
              <Form.Item name="supplier_id" label="Calibration supplier">
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="Approved calibration laboratory"
                  options={suppliers.map((s) => ({
                    value: s.id,
                    label: s.supplier_code ? `${s.supplier_code} — ${s.supplier_name}` : s.supplier_name,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="calibration_provider" label="Provider (free text)">
                <Input placeholder="When not a registered supplier" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="calibration_location" label="Calibration location">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="external_limitations" label="Limitations on use">
                <TextArea rows={2} placeholder="Any restriction on range or application" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Space style={{ marginTop: 8 }}>
          <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saving}>
            Save schedule
          </Button>
          <Button onClick={backTo}>Cancel</Button>
          {isEdit && canCancel && existing?.status !== 'Cancelled' && (
            <Button danger icon={<StopOutlined />} onClick={confirmCancel} loading={cancelMutation.isPending}>
              Cancel schedule
            </Button>
          )}
        </Space>
      </Form>
    </div>
  );
};

export default CalibrationScheduleFormPage;
